<script setup lang="ts">
  import { computed, ref, watch } from 'vue';

  import type { Email, EmailSender, Partner, User } from '@stuut/api/models';
  import { Button } from '@ui/components/button';
  import { MultiEmailInput } from '@ui/components/email-input';
  import { Input } from '@ui/components/input';
  import { Label } from '@ui/components/label';
  import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
  } from '@ui/components/select';

  import ContactTagsList from '@/components/common/ContactTagsList.vue';
  import { EmailSenderSelect } from '@/components/email-senders';
  import { detectLanguageAndDirection } from '@/components/emails/utils';

  const props = withDefaults(
    defineProps<{
      emailSender?: EmailSender | null;
      parentEmail?: Email | null;
      fromEmail?: string;
      validSenders?: EmailSender[];
      readonlyFrom?: boolean;
      to?: string[];
      cc?: string[];
      bcc?: string[];
      subject?: string;
      partner?: Partner;
      users?: User[];
      hideFrom?: boolean;
      hideSubject?: boolean;
      hideTo?: boolean;
      hideBcc?: boolean;
      readonlySubject?: boolean;
      readonlyTo?: boolean;
      suggestContacts?: boolean;
      disabled?: boolean;
    }>(),
    {
      emailSender: null,
      parentEmail: null,
      fromEmail: '',
      validSenders: undefined,
      to: () => [],
      cc: () => [],
      bcc: () => [],
      subject: '',
      users: () => [],
    },
  );

  const emit = defineEmits<{
    (e: 'update:emailSender', value: EmailSender | null): void;
    (e: 'update:fromEmail', value: string): void;
    (e: 'update:to', value: string[]): void;
    (e: 'update:cc', value: string[]): void;
    (e: 'update:bcc', value: string[]): void;
    (e: 'update:subject', value: string): void;
  }>();

  const useScopedFromSelect = computed(() => props.validSenders !== undefined);

  const showCc = ref((props.cc?.length ?? 0) > 0);
  const showBcc = ref(!props.hideBcc && (props.bcc?.length ?? 0) > 0);

  watch(
    () => props.cc,
    (v) => {
      if ((v?.length ?? 0) > 0) showCc.value = true;
    },
  );

  watch(
    () => props.bcc,
    (v) => {
      if (!props.hideBcc && (v?.length ?? 0) > 0) showBcc.value = true;
    },
  );

  const subjectTextDir = computed(() => detectLanguageAndDirection(props.subject ?? '').direction);

  const onCcBlur = () => {
    showCc.value = (props.cc?.length ?? 0) > 0;
  };

  const onBccBlur = () => {
    showBcc.value = (props.bcc?.length ?? 0) > 0;
  };
</script>

<template>
  <div class="flex flex-col">
    <div class="flex flex-col gap-y-2">
      <div v-if="!hideFrom" class="flex gap-x-2 items-center">
        <Label class="w-[60px] text-xs">From</Label>

        <template v-if="useScopedFromSelect">
          <MultiEmailInput
            v-if="readonlyFrom || !validSenders?.length"
            :model-value="[fromEmail]"
            disabled
          />

          <Select
            v-else
            class="grow px-1 h-5"
            :model-value="fromEmail"
            :disabled="disabled"
            @update:model-value="(v) => emit('update:fromEmail', String(v))"
          >
            <SelectTrigger class="w-full border! border-inherit focus:outline-hidden">
              <SelectValue />
            </SelectTrigger>

            <SelectContent>
              <SelectGroup>
                <SelectItem
                  v-for="sender in validSenders"
                  :key="sender.email"
                  :value="sender.email"
                >
                  {{ sender.email }}
                </SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </template>

        <EmailSenderSelect
          v-else
          class="h-10.5"
          :model-value="emailSender"
          :email="parentEmail"
          :disabled="disabled"
          @update:model-value="(v) => emit('update:emailSender', v)"
        />
      </div>

      <div v-if="!hideSubject" class="flex gap-2 items-center">
        <Label class="w-[60px] text-xs">Subject</Label>

        <Input
          :dir="subjectTextDir"
          :model-value="subject"
          placeholder="Subject"
          :disabled="disabled || readonlySubject"
          @update:model-value="(v) => emit('update:subject', String(v))"
        />
      </div>

      <div class="flex flex-1 gap-3 items-baseline">
        <div v-if="!hideTo" class="flex flex-1 gap-2 items-baseline">
          <Label class="w-[60px] text-xs">To</Label>

          <ContactTagsList
            class="w-full text-xs"
            :partner="partner"
            :users="users"
            :model-value="to"
            :disabled="disabled || readonlyTo"
            :suggest-contacts="suggestContacts"
            @update:model-value="(v) => emit('update:to', v)"
          />
        </div>

        <div
          v-if="!showCc || (!showBcc && !hideBcc)"
          class="flex gap-x-1"
          :class="hideTo && 'ml-15'"
        >
          <Button
            v-if="!showCc"
            variant="ghost"
            size="icon-xs"
            :disabled="disabled"
            @click="showCc = true"
          >
            Cc
          </Button>

          <Button
            v-if="!showBcc && !hideBcc"
            variant="ghost"
            size="icon-xs"
            :disabled="disabled"
            @click="showBcc = true"
          >
            Bcc
          </Button>
        </div>
      </div>
    </div>

    <div v-auto-animate class="space-y-2" :class="showCc || showBcc ? 'mt-2' : ''">
      <div v-if="showCc" class="flex flex-1 gap-2 items-center">
        <Label class="w-[60px] text-xs">Cc</Label>

        <ContactTagsList
          class="w-full text-xs"
          :partner="partner"
          :users="users"
          :model-value="cc"
          :disabled="disabled"
          :suggest-contacts="suggestContacts"
          @update:model-value="(v) => emit('update:cc', v)"
          @blur="onCcBlur"
        />
      </div>

      <div v-if="showBcc && !hideBcc" class="flex flex-1 gap-2 items-center">
        <Label class="w-[60px] text-xs">Bcc</Label>

        <ContactTagsList
          class="w-full text-xs"
          :partner="partner"
          :users="users"
          :model-value="bcc"
          :disabled="disabled"
          :suggest-contacts="suggestContacts"
          @update:model-value="(v) => emit('update:bcc', v)"
          @blur="onBccBlur"
        />
      </div>
    </div>
  </div>
</template>
